import { memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiList } from "@miplatform/components";
import { useFeatureFlags } from "@miplatform/feature-flags";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { IssueUsersHeader } from "../IssueUsersHeader";
import { PrebillItem } from "./PrebillItem";
import { PREBILL_ITEM_ORDER_MAP, PREBILL_ITEM_TYPE } from "./prebillConstants";

export const IssuePrebill = memo(
  ({
    isDeleteDisabled,
    attempt,
    laborRates,
    issue,
    index,
    invoice,
    users,
    onUpdate,
    onDelete,
  }) => {
    const { classes } = useStyles();
    const { originalDescription: description = "--" } = issue;
    const { featureFlags } = useFeatureFlags();
    const isMiDayFeatureEnabled = featureFlags?.miDayEnabled;
    const materialOptions = useMemo(
      () =>
        (issue.materialReferences || [])
          .filter((materialReference) => materialReference.isUsed)
          .map((materialReference) => {
            const name = [
              materialReference.brand,
              materialReference.description,
            ]
              .filter(Boolean)
              .join(" | ");

            return {
              value: name,
              label: name,
              quantity: materialReference.quantity,
            };
          })
          .filter((material) => material.value),
      [issue.materialReferences]
    );
    const totalBeforeDiscount = useMemo(
      () =>
        invoice?.subItems?.reduce((acc, item) => {
          if (item.type !== PREBILL_ITEM_TYPE.DISCOUNT && !item.isNew) {
            return acc + item.totalPrice;
          }

          return acc;
        }, 0),
      [invoice]
    );

    return (
      <Box className={classes.root}>
        <IssueUsersHeader
          title={`${index + 1}. Closeout: ${issue.title}`}
          attempt={{
            ...attempt,
            issues: attempt.unfilteredIssues || attempt.issues,
          }}
          issue={issue}
          users={users}
          showBillableToggle={isMiDayFeatureEnabled}
        />
        <Box className={classes.body}>
          <Typography variant="caption">{description}</Typography>
        </Box>
        {Boolean(invoice?.subItems?.length) && (
          <Box>
            <MiList
              groupedMode
              viewType="card"
              hideListPagination
              groupedCriteria="type"
              data={invoice?.subItems}
              CardComponent={PrebillItem}
              groupedDataSort={(a, b) =>
                PREBILL_ITEM_ORDER_MAP[a[0]] - PREBILL_ITEM_ORDER_MAP[b[0]]
              }
              cardProps={{
                total: totalBeforeDiscount,
                laborRates,
                materialOptions,
                isDeleteDisabled,
                onUpdate: (item) => onUpdate(item, index),
                onDelete: (item) => onDelete(item, index),
              }}
              cardListContainerProps={{ className: "" }}
            />
          </Box>
        )}
        <Box className={classes.footer}>
          <Typography fontWeight="bold" variant="body1" color="grey.medium">
            Total Price:
          </Typography>
          <Typography fontWeight="600">
            {(invoice?.totalPrice ?? 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              currency: "USD",
              style: "currency",
            })}
          </Typography>
        </Box>
      </Box>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.grey.light,
    padding: theme.spacing(2, 3),
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: theme.shape.borderRadius,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    flexWrap: "wrap",
    borderTop: `1px solid ${theme.palette.grey.darkAccent}`,
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    width: "100%",
    gap: theme.spacing(2),
    padding: theme.spacing(4),
  },
}));
